import { action, observable, makeObservable } from 'mobx';
import { type IkeClientService, Services } from './index';

const MAP_TYPE_KEY_PREFIX = 'mapTypeId:';

const MAP_OPTIONS: google.maps.MapOptions = {
	/* Disable clicking points of interest. This makes it confusing when trying to click on collections. */
	clickableIcons: false,
	/* Disable 45° camera angle in satellite map mode. Only top-down angle will position collections accurately. */
	tilt: 0,
	/* Feature works but is not tested. Disable until asked for. */
	rotateControl: false,
};

export class MapService implements IkeClientService {
	__priority = 100;

	private mapInstance?: google.maps.Map;

	@observable isReady = false;

	/** Promise for when google maps is ready */
	isReadyPromise!: Promise<void>;

	constructor() {
		makeObservable(this);
	}

	init() {
		this.isReadyPromise = new Promise((resolve) => {
			const waitForGoogleMaps = Services.Window.window.setInterval(() => {
				if (typeof google === 'undefined') {
					return;
				}
				Services.Window.window.clearTimeout(waitForGoogleMaps);
				this.setIsReady();
				resolve();
			}, 10);
		});
	}

	/**
	 * Fetch Google Map instance, reusing existing instance to avoid impacting Google Maps API quota.
	 */
	get map(): google.maps.Map {
		if (!this.isReady) {
			throw new Error('Cannot fetch map before Google Maps API loaded');
		}

		if (this.mapInstance == null) {
			const mapEl = Services.Window.document.createElement('div');
			mapEl.style.width = '100%';
			mapEl.style.height = '100%';

			this.mapInstance = new google.maps.Map(mapEl, MAP_OPTIONS);
		}

		return this.mapInstance;
	}

	attach(el: HTMLElement): google.maps.Map {
		const map = this.map;
		if (map == null) {
			return null;
		}

		el.appendChild(map.getDiv());
		return map;
	}

	@action setIsReady() {
		this.isReady = true;
	}

	getMapType(context: string, defaultType: string): string {
		return Services.LocalStorage.getItem<string>(MAP_TYPE_KEY_PREFIX + context) || defaultType;
	}

	listenMapType(context: string): google.maps.MapsEventListener {
		return this.map.addListener('maptypeid_changed', () => {
			const mapTypeId = this.map.getMapTypeId();
			Services.LocalStorage.setItem(MAP_TYPE_KEY_PREFIX + context, mapTypeId);
		});
	}
}
